import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

@Component({
  selector: 'hb-input-money',
  templateUrl: './input-money.component.html',
  styleUrls: ['./input-money.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputMoneyComponent implements AfterViewInit {
  /**
   * Reference used in the template to the Add Stepper
   */
  @ViewChild('addStepper')
  public addStepper: ElementRef<HTMLButtonElement>;

  /**
   * Reference used in the template to the Substract Stepper
   */
  @ViewChild('substractStepper')
  public substractStepper: ElementRef<HTMLButtonElement>;

  @Input() public label?: string;
  @Input() public value: number;
  @Input() public prefix = '';
  @Input() public suffix = '';
  @Input() public placeholder?: string;

  @Input() public disabled?: boolean;
  @Input() public isError?: boolean;

  /**
   * Will add attributes to the input to hide it from SmartLook records
   */
  @Input() public sensitive = true;

  @Input() public min = 0;
  @Input() public max = 999999999999999;

  @Input() public restrictToIntegers = false;

  @Input() public addSteppers: boolean;
  /** Used when short-pressing the steppers */
  @Input() private stepSize = 1;
  /** Used when long-pressing the steppers */
  @Input() private bigStepSize = 10;

  @Output() private valueChange: EventEmitter<number> =
    new EventEmitter<number>();

  /** Time threshold when pressing an stepper to use the bigStepSize */
  private longPressDuration = 300;

  /** Adds the --focus modifier to the input even when it's not :focused  */
  public setInputAsFocused: boolean;

  ngAfterViewInit() {
    // We use ngAfterViewInit to allow the steppers to be rendered in view
    // before referencing them to add their handlers
    if (this.addSteppers) {
      this.addStepperEventHandlers(
        this.addStepper.nativeElement,
        this.stepSize,
        this.bigStepSize,
      );
      this.addStepperEventHandlers(
        this.substractStepper.nativeElement,
        -this.stepSize,
        -this.bigStepSize,
      );

      this.addInputFocuserToStepper(this.addStepper.nativeElement);
      this.addInputFocuserToStepper(this.substractStepper.nativeElement);
    }
  }

  public handleValueChange(value: string) {
    this.valueChange.next(Number(value));
  }

  /**
   * Adds the short/long-click handlers to the stepper
   *
   * @param stepper that will get the event handlers attached
   * @param stepSize when short-pressing
   * @param bigStepSize when long-pressing
   */
  private addStepperEventHandlers(
    stepper: HTMLButtonElement,
    stepSize: number,
    bigStepSize: number,
  ) {
    let longPressInterval: NodeJS.Timer;
    let isLongPressActive = false;

    const isLeftButtonClick = (event: MouseEvent) => event.button === 0;

    const closeInterval = () => {
      clearInterval(longPressInterval);
      isLongPressActive = false;
    };

    stepper.onmousedown = (event) => {
      if (!isLeftButtonClick(event)) {
        return;
      }
      longPressInterval = setInterval(() => {
        this.stepCurrentValue(event, bigStepSize);
        isLongPressActive = true;
      }, this.longPressDuration);
    };

    stepper.onmouseup = (event) => {
      if (!isLeftButtonClick(event)) {
        return;
      }
      if (!isLongPressActive) {
        this.stepCurrentValue(event, stepSize);
      }
      closeInterval();
    };

    stepper.onmouseleave = () => {
      closeInterval();
    };
  }

  /**
   * Adds the custom logic to take stepper:focus as input:focus
   */
  private addInputFocuserToStepper(stepper: HTMLButtonElement) {
    stepper.onfocus = () => {
      this.setInputAsFocused = true;
    };
    stepper.onblur = () => {
      this.setInputAsFocused = false;
    };
  }

  private stepCurrentValue(event: Event, step: number) {
    // Prevent that the click event gets triggered in  the wrapping element,
    // and skip the default handler
    event.stopPropagation();

    this.value = Math.min(Math.max(this.min, this.value + step), this.max);
    this.valueChange.emit(this.value);
  }
}
